import { useState } from "react";
import { PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from "recharts";
import { admin as adminApi, analytics as analyticsApi, users as usersApi, subscriptions as subsApi, books as booksApi } from "../lib/api.js";
import { useApi } from "../hooks/index.js";
import { Spinner, Empty, Badge, CloseX } from "../components/ui.jsx";
import { useLang } from "../lib/i18n.jsx";

const COLORS = ["#0db87e", "#f5a623", "#2563eb", "#e03050", "#8aaa98"];
const HEX_COLORS = COLORS;

// ─── STAT CARD ────────────────────────────────────────────────────────────────

function StatCard({ cls, label, value, sub, chg }) {
  return (
    <div className={`stat-card ${cls}`}>
      <div className="sc-lbl">{label}</div>
      <div className="sc-val">{value}</div>
      <div className="sc-sub">{sub}</div>
      <span className="sc-chg chg-up">{chg}</span>
    </div>
  );
}

// ─── FUNNEL DETAIL MODAL ──────────────────────────────────────────────────────

function FunnelDetailModal({ step, allFunnel, onClose }) {
  const { t } = useLang();
  const { data, loading } = useApi(() => usersApi.list({ limit: 100 }));
  const users = data?.users ?? [];

  // Determine which users match this funnel step
  const filteredUsers = (() => {
    if (!step) return users;
    const s = step.step?.toLowerCase() ?? "";
    if (s.includes("registered") || s.includes("sign")) return users;
    if (s.includes("active") || s.includes("subscrib")) return users.filter(u => u.subscriptionStatus === "ACTIVE" || u.subscriptionStatus === "TRIAL");
    if (s.includes("complet") || s.includes("module")) return users.filter(u => u.subscriptionStatus === "ACTIVE");
    if (s.includes("engage") || s.includes("return")) return users.filter(u => ["ACTIVE", "TRIAL"].includes(u.subscriptionStatus));
    return users;
  })();

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 580 }}>
        <div className="modal-ttl">
          🎯 {t("analytics.funnel", { step: step?.step ?? "" })}
          <CloseX onClick={onClose} />
        </div>

        {/* Funnel step stats */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
          {[
            ["👥 Users at Step", (step?.count ?? 0).toLocaleString(), "var(--teal)"],
            ["📊 Conversion",    `${step?.pct ?? 0}%`,               "var(--amber)"],
            ["📉 Drop",          step?.drop ? `${step.drop}%` : "—", "var(--rose)"],
          ].map(([l, v, c]) => (
            <div key={l} style={{ textAlign: "center", padding: 12, background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
              <div style={{ fontFamily: "var(--fd)", fontSize: 20, fontWeight: 900, color: c }}>{v}</div>
              <div style={{ fontSize: 9, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".5px", marginTop: 2 }}>{l}</div>
            </div>
          ))}
        </div>

        {/* All funnel steps for context */}
        <div style={{ marginBottom: 16, padding: 12, background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 8 }}>{t("analytics.fullFunnel")}</div>
          {allFunnel.map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", background: f.step === step?.step ? "var(--teal)" : "var(--teal-dim)", color: f.step === step?.step ? "white" : "var(--teal)", fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</div>
              <span style={{ fontSize: 12, flex: 1, fontWeight: f.step === step?.step ? 700 : 400, color: f.step === step?.step ? "var(--ink)" : "var(--muted)" }}>{f.step}</span>
              <span style={{ fontFamily: "var(--fm)", fontSize: 11, fontWeight: 700, color: f.step === step?.step ? "var(--teal)" : "var(--ghost)" }}>{f.pct}%</span>
            </div>
          ))}
        </div>

        {/* Users at this step */}
        {loading ? <Spinner /> : filteredUsers.length === 0 ? <Empty icon="👥" message={t("ui.noData")} /> : (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 8 }}>
              {t("analytics.matchingUsers", { n: filteredUsers.length })}
            </div>
            <div className="tbl-wrap" style={{ maxHeight: 260, overflowY: "auto" }}>
              <table>
                <thead><tr><th>{t("tbl.user")}</th><th>{t("tbl.status")}</th><th>{t("tbl.plan")}</th><th>{t("tbl.platform")}</th><th>{t("tbl.joined")}</th></tr></thead>
                <tbody>
                  {filteredUsers.slice(0, 30).map(u => (
                    <tr key={u.id}>
                      <td><div style={{ fontWeight: 700, fontSize: 12 }}>{u.name}</div><div style={{ fontSize: 10, color: "var(--ghost)" }}>{u.email}</div></td>
                      <td><Badge status={u.subscriptionStatus} /></td>
                      <td style={{ fontSize: 11 }}>{u.subscription?.plan ?? "—"}</td>
                      <td>{u.subscription?.platform ? <span className="badge bg-blue" style={{ fontSize: 9 }}>{u.subscription.platform}</span> : <span style={{ color: "var(--ghost)", fontSize: 11 }}>—</span>}</td>
                      <td style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PLATFORM DETAIL MODAL ────────────────────────────────────────────────────

function PlatformDetailModal({ platform, allPlatforms, onClose }) {
  const { t } = useLang();
  const { data, loading } = useApi(
    () => subsApi.list({ platform: platform?.platform, limit: 100 }),
    [platform?.platform]
  );
  const subs = data?.subscriptions ?? [];
  const total = data?.total ?? 0;
  const colors = ["var(--teal)", "var(--amber)", "var(--blue)", "var(--rose)", "var(--ghost)"];

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 580 }}>
        <div className="modal-ttl">
          💳 {platform?.platform ?? ""} Subscribers
          <CloseX onClick={onClose} />
        </div>

        {/* Platform stats */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
          {[
            ["📊 Market Share", `${platform?.pct ?? 0}%`,                "var(--teal)"],
            ["👥 Subscribers",  (platform?.count ?? 0).toLocaleString(), "var(--amber)"],
            ["📈 Est. Revenue", `$${((platform?.count ?? 0) * 14).toLocaleString()}`, "var(--blue)"],
          ].map(([l, v, c]) => (
            <div key={l} style={{ textAlign: "center", padding: 12, background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
              <div style={{ fontFamily: "var(--fd)", fontSize: 20, fontWeight: 900, color: c }}>{v}</div>
              <div style={{ fontSize: 9, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".5px", marginTop: 2 }}>{l}</div>
            </div>
          ))}
        </div>

        {/* All platforms for context */}
        <div style={{ marginBottom: 16, padding: 12, background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 10 }}>{t("analytics.allPlatformsBreakdown")}</div>
          {allPlatforms.map((p, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: colors[i % colors.length] }} />
                  <span style={{ fontSize: 12, color: p.platform === platform?.platform ? "var(--ink)" : "var(--muted)", fontWeight: p.platform === platform?.platform ? 700 : 400 }}>{p.platform}</span>
                </div>
                <span style={{ fontFamily: "var(--fm)", fontSize: 11, fontWeight: 700 }}>{p.pct}% <span style={{ color: "var(--ghost)" }}>({p.count})</span></span>
              </div>
              <div className="prog-track">
                <div className="prog-fill" style={{ width: `${p.pct}%`, background: p.platform === platform?.platform ? colors[i % colors.length] : "var(--border)" }} />
              </div>
            </div>
          ))}
        </div>

        {/* Subscribers on this platform */}
        {loading ? <Spinner /> : subs.length === 0 ? <Empty icon="💳" message="No subscribers on this platform" /> : (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 8 }}>
              {platform?.platform} Subscribers ({total})
            </div>
            <div className="tbl-wrap" style={{ maxHeight: 240, overflowY: "auto" }}>
              <table>
                <thead><tr><th>{t("tbl.user")}</th><th>{t("tbl.plan")}</th><th>{t("tbl.status")}</th><th>{t("tbl.expires")}</th></tr></thead>
                <tbody>
                  {subs.slice(0, 30).map(s => (
                    <tr key={s.id}>
                      <td><div style={{ fontWeight: 700, fontSize: 12 }}>{s.user?.name ?? "—"}</div><div style={{ fontSize: 10, color: "var(--ghost)" }}>{s.user?.email}</div></td>
                      <td><span className="badge bg-teal">{s.plan}</span></td>
                      <td><Badge status={s.status} /></td>
                      <td style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>{s.periodEnd ? new Date(s.periodEnd).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > 30 && <div style={{ paddingTop: 8, fontSize: 11, color: "var(--ghost)", textAlign: "center" }}>{t("ui.showingOf", { n: 30, total })}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── RETENTION COHORT MODAL ───────────────────────────────────────────────────

function RetentionCohortModal({ cohort, allCohorts, onClose }) {
  const { t } = useLang();
  const retVal = (v) =>
    v === null ? "—"
    : v >= 60  ? `${v}% ✅`
    : v >= 30  ? `${v}% ⚠️`
    :             `${v}% 🔴`;

  const retColor = (v) =>
    v === null ? "var(--ghost)" : v >= 60 ? "#065f46" : v >= 30 ? "#92400e" : "#9f1239";
  const retBg = (v) =>
    v === null ? "#f3f4f6" : v >= 60 ? "#d1fae5" : v >= 30 ? "var(--amber-lo)" : "var(--rose-lo)";

  // Best and worst day
  const days = ["d1","d7","d14","d30"].map(k => ({ k, v: cohort?.[k] ?? null })).filter(d => d.v !== null);
  const best  = days.reduce((a, b) => (b.v > (a?.v ?? -1) ? b : a), null);
  const worst = days.reduce((a, b) => (b.v < (a?.v ?? 101) ? b : a), null);

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 580 }}>
        <div className="modal-ttl">
          🔁 {t("analytics.cohort", { cohort: cohort?.cohort ?? "" })}
          <CloseX onClick={onClose} />
        </div>

        {/* Retention grid for this cohort */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
          {[["Day 1","d1"],["Day 7","d7"],["Day 14","d14"],["Day 30","d30"]].map(([lbl, key]) => {
            const v = cohort?.[key] ?? null;
            return (
              <div key={key} style={{ textAlign: "center", padding: 12, background: retBg(v), borderRadius: 8, border: "1px solid var(--border)" }}>
                <div style={{ fontFamily: "var(--fd)", fontSize: 22, fontWeight: 900, color: retColor(v) }}>{v !== null ? `${v}%` : "—"}</div>
                <div style={{ fontSize: 9, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".5px", marginTop: 2 }}>{lbl}</div>
              </div>
            );
          })}
        </div>

        {/* Insights */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          <div style={{ padding: 12, background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
            <div style={{ fontSize: 10, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 4 }}>{t("analytics.cohortSize")}</div>
            <div style={{ fontFamily: "var(--fd)", fontSize: 20, fontWeight: 900, color: "var(--teal)" }}>{cohort?.users ?? 0}</div>
            <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>{t("analytics.usersInCohort")}</div>
          </div>
          <div style={{ padding: 12, background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
            <div style={{ fontSize: 10, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 4 }}>{t("analytics.30dayRetention")}</div>
            <div style={{ fontFamily: "var(--fd)", fontSize: 20, fontWeight: 900, color: retColor(cohort?.d30 ?? null) }}>
              {cohort?.d30 != null ? `${cohort.d30}%` : t("analytics.pending")}
            </div>
            <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
              {cohort?.d30 != null ? (cohort.d30 >= 60 ? t("analytics.excellentRetention") : cohort.d30 >= 30 ? t("analytics.needsImprovement") : t("analytics.highChurnRisk")) : t("analytics.notEnoughData")}
            </div>
          </div>
        </div>

        {/* Trend visualization */}
        <div style={{ padding: 14, background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)", marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 10 }}>{t("analytics.retentionCurve")}</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 60 }}>
            {[["D1",cohort?.d1],["D7",cohort?.d7],["D14",cohort?.d14],["D30",cohort?.d30]].map(([lbl,v]) => (
              <div key={lbl} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, height: "100%" }}>
                <div style={{ flex: 1, width: "100%", display: "flex", alignItems: "flex-end" }}>
                  <div style={{ width: "100%", background: v != null ? (v >= 60 ? "var(--teal)" : v >= 30 ? "var(--amber)" : "var(--rose)") : "var(--border)", height: v != null ? `${v}%` : "4px", borderRadius: "4px 4px 0 0", minHeight: 4 }} />
                </div>
                <div style={{ fontSize: 9, color: "var(--ghost)", fontFamily: "var(--fm)" }}>{lbl}</div>
              </div>
            ))}
          </div>
        </div>

        {/* All cohorts comparison table */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 8 }}>{t("analytics.allCohortsComparison")}</div>
          <div className="tbl-wrap" style={{ maxHeight: 200, overflowY: "auto" }}>
            <table>
              <thead><tr><th>Cohort</th><th>Users</th><th>D1</th><th>D7</th><th>D14</th><th>D30</th></tr></thead>
              <tbody>
                {allCohorts.map(r => (
                  <tr key={r.cohort} style={{ background: r.cohort === cohort?.cohort ? "var(--teal-dim)" : "" }}>
                    <td style={{ fontWeight: r.cohort === cohort?.cohort ? 700 : 400 }}>{r.cohort}</td>
                    <td style={{ fontFamily: "var(--fm)" }}>{r.users}</td>
                    {["d1","d7","d14","d30"].map(k => {
                      const v = r[k] ?? null;
                      return (
                        <td key={k}>
                          {v === null
                            ? <span style={{ fontSize: 10, color: "var(--ghost)" }}>—</span>
                            : <span style={{ background: retBg(v), color: retColor(v), fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10, display: "inline-block" }}>{v}%</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN ANALYTICS PAGE ──────────────────────────────────────────────────────

const PLT_LABEL_A = { apple_iap: "Apple IAP", google_play: "Google Play", stripe: "Stripe", flutterwave: "Flutterwave", scholarship: "Scholarship", manual: "Manual" };
const PLT_COLOR_A = { apple_iap: "#1d1d1f", google_play: "#34a853", stripe: "#635bff", flutterwave: "#f5a623", scholarship: "#0db87e", manual: "#8aaa98" };

export default function PageAnalytics() {
  const { t } = useLang();
  const { data: ov, loading } = useApi(() => analyticsApi.overview());
  const { data: fd }          = useApi(() => analyticsApi.funnel());
  const { data: rd }          = useApi(() => analyticsApi.retention());
  const { data: ps }          = useApi(() => adminApi.paymentStats());
  const { data: mm }          = useApi(() => analyticsApi.mobileMoney());
  const { data: bd }          = useApi(() => booksApi.downloadStats());

  const [selFunnel,   setSelFunnel]   = useState(null);
  const [selPlatform, setSelPlatform] = useState(null);
  const [selCohort,   setSelCohort]   = useState(null);

  if (loading) return <Spinner />;

  const o        = ov ?? {};
  const funnel   = fd?.funnel ?? [];
  const cohorts  = rd?.cohorts ?? [];
  const platforms = ps?.stats ?? [];
  const mmStats   = mm?.providers ?? [];

  return (
    <div>
      {/* Stat Cards */}
      <div className="stats-grid">
        <StatCard cls="sc-teal"  label={t("analytics.dau")}        value={o.dau ?? 0}              sub={t("analytics.dailyActive")}    chg={t("analytics.liveFromDb")} />
        <StatCard cls="sc-teal"  label={t("analytics.mau")}        value={o.mau ?? 0}              sub={t("analytics.monthlyActive")}  chg={t("analytics.activeSubscribers")} />
        <StatCard cls="sc-amber" label={t("analytics.stickiness")} value={`${o.stickiness ?? 0}%`} sub={t("analytics.dauMauRatio")}    chg={t("analytics.targetStickiness")} />
        <StatCard cls="sc-blue"  label={t("analytics.avgSession")} value={`${o.avgSessionMin ?? 0}m`} sub={t("analytics.sessionLength")} chg={t("analytics.postHog")} />
      </div>

      <div className="cg2">
        {/* Funnel — clickable rows */}
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-hdr">
            <div className="card-ttl"><div className="card-ico">🎯</div>{t("analytics.subscriptionFunnel")}</div>
            <span style={{ fontSize: 11, color: "var(--ghost)" }}>{t("analytics.clickForDetails")}</span>
          </div>
          <div className="card-body">
            {funnel.length === 0 ? <Empty icon="🎯" message="Funnel data loading…" /> : funnel.map((f, i) => (
              <div key={i} className="funnel-row"
                onClick={() => setSelFunnel(f)}
                style={{ cursor: "pointer", padding: "8px 6px", borderRadius: 8, transition: "background .15s", marginBottom: 4 }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--teal-dim)"}
                onMouseLeave={e => e.currentTarget.style.background = ""}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div className="funnel-num">{i + 1}</div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink2)" }}>{f.step}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontFamily: "var(--fm)", fontSize: 12, fontWeight: 700 }}>{(f.count || 0).toLocaleString()} ({f.pct}%)</span>
                    <span style={{ fontSize: 12, color: "var(--ghost)" }}>›</span>
                  </div>
                </div>
                <div className="prog-track" style={{ height: 8 }}>
                  <div className="prog-fill" style={{ width: `${f.pct}%` }} />
                </div>
                {f.drop && <div style={{ fontSize: 10, color: "var(--rose)", marginTop: 2 }}>▼ {f.drop}% dropped</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Revenue by Platform — PieChart + clickable legend */}
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-hdr">
            <div className="card-ttl"><div className="card-ico">💳</div>{t("analytics.revenueByPlatform")}</div>
            <span style={{ fontSize: 11, color: "var(--ghost)" }}>{t("analytics.clickSegment")}</span>
          </div>
          <div className="card-body">
            {platforms.length === 0 ? <Empty icon="💳" message="No subscription data yet" /> : (
              <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <ResponsiveContainer width={180} height={180} minWidth={140}>
                  <PieChart>
                    <Pie
                      data={platforms}
                      dataKey="count"
                      nameKey="platform"
                      cx="50%"
                      cy="50%"
                      outerRadius={78}
                      innerRadius={44}
                      paddingAngle={3}
                      onClick={p => setSelPlatform(p)}
                      style={{ cursor: "pointer" }}
                    >
                      {platforms.map((_, i) => (
                        <Cell key={i} fill={HEX_COLORS[i % HEX_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0].payload;
                        return (
                          <div style={{ background: "var(--ink)", color: "white", borderRadius: 8, padding: "6px 12px", fontSize: 12 }}>
                            <div style={{ fontWeight: 700 }}>{p.platform}</div>
                            <div style={{ color: "var(--teal)" }}>{p.count} · {p.pct}%</div>
                          </div>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ flex: 1, minWidth: 100 }}>
                  {platforms.map((p, i) => (
                    <div key={i}
                      onClick={() => setSelPlatform(p)}
                      style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer", padding: "6px 8px", borderRadius: 8, transition: "background .15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--surface)"}
                      onMouseLeave={e => e.currentTarget.style.background = ""}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: HEX_COLORS[i % HEX_COLORS.length], flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.platform}</div>
                        <div style={{ fontSize: 10, color: "var(--ghost)", fontFamily: "var(--fm)" }}>{p.count} · {p.pct}%</div>
                      </div>
                      <span style={{ fontSize: 11, color: "var(--ghost)" }}>›</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Subscription platform bar chart ── */}
      {platforms.length > 0 && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-hdr">
            <div className="card-ttl"><div className="card-ico">📈</div>{t("analytics.platformPerformance")}</div>
            <span style={{ fontSize: 11, color: "var(--ghost)" }}>{t("analytics.activeBySource")}</span>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={120}>
              <BarChart
                data={platforms.map((p, i) => ({ name: PLT_LABEL_A[p.platform] ?? p.platform, Subscribers: p.count, pct: p.pct, fill: PLT_COLOR_A[p.platform] ?? COLORS[i % COLORS.length] }))}
                margin={{ top: 4, right: 4, left: -20, bottom: 4 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--ghost)", fontFamily: "var(--fb)" }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip
                  contentStyle={{ background: "var(--ink)", border: "none", borderRadius: 8, padding: "8px 12px" }}
                  labelStyle={{ color: "white", fontWeight: 700, fontSize: 12 }}
                  itemStyle={{ color: "var(--teal)", fontSize: 12 }}
                  formatter={(value, _name, props) => [`${value} subs (${props.payload.pct}%)`, "Subscribers"]}
                />
                <Bar dataKey="Subscribers" radius={[6, 6, 0, 0]}>
                  {platforms.map((p, i) => <Cell key={i} fill={PLT_COLOR_A[p.platform] ?? COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
              {platforms.map((p, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, background: "var(--surface)", border: "1px solid var(--border)", cursor: "pointer" }}
                  onClick={() => setSelPlatform(p)}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: PLT_COLOR_A[p.platform] ?? COLORS[i % COLORS.length], flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>{PLT_LABEL_A[p.platform] ?? p.platform}</span>
                  <span style={{ fontFamily: "var(--fm)", fontSize: 11, fontWeight: 700, color: "var(--ink)" }}>{p.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile Money provider analytics ── */}
      {mmStats.length > 0 && (() => {
        const MM_LABEL = { mpesa: "M-Pesa", mtn_momo: "MTN MoMo", airtel_money: "Airtel Money", vodacom_mpesa: "Vodacom M-Pesa" };
        // _count comes back as { _all: N } when using _count: { _all: true } in groupBy
        const getCount = (p) => (typeof p._count === 'number' ? p._count : (p._count?._all ?? p.count ?? 0));
        const total = mmStats.reduce((a, p) => a + getCount(p), 0);
        return (
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="card-hdr">
              <div className="card-ttl"><div className="card-ico">📱</div>{t("analytics.mobileMoneyProviders")}</div>
              <span style={{ fontSize: 11, color: "var(--ghost)" }}>{t("analytics.activeMobileMoneySubscribers", { n: total })}</span>
            </div>
            <div className="card-body" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
              {mmStats.map((p, i) => {
                const name  = p.mobileMoneyProvider ?? p.provider ?? p.platform ?? "—";
                const count = getCount(p);
                const pct   = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <div key={i} style={{ textAlign: "center", padding: 14, background: "var(--surface)", borderRadius: 10, border: "1px solid var(--border)" }}>
                    <div style={{ fontFamily: "var(--fd)", fontSize: 24, fontWeight: 900, color: COLORS[i % COLORS.length], marginBottom: 2 }}>{count}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>{MM_LABEL[name] ?? name}</div>
                    <div style={{ height: 4, background: "var(--border)", borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ height: "100%", background: COLORS[i % COLORS.length], borderRadius: 2, width: `${pct}%` }} />
                    </div>
                    <div style={{ fontSize: 10, color: "var(--ghost)", marginTop: 4 }}>{pct}% {t("analytics.pctShare")}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Retention summary insights ── */}
      {cohorts.length > 0 && (() => {
        const latest = cohorts[cohorts.length - 1];
        const avg30 = cohorts.filter(c => c.d30 != null).reduce((a, c) => a + c.d30, 0) / Math.max(1, cohorts.filter(c => c.d30 != null).length);
        return (
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="card-hdr">
              <div className="card-ttl"><div className="card-ico">🔄</div>{t("analytics.retentionSummary")}</div>
              <span style={{ fontSize: 11, color: "var(--ghost)" }}>{t("analytics.cohortsTracked", { n: cohorts.length })}</span>
            </div>
            <div className="card-body" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
              {[
                { label: t("analytics.latestCohort"),   value: latest?.cohort ?? "—",                        sub: `${latest?.users ?? 0} users`,     color: "var(--ink)" },
                { label: t("analytics.day1Retention"),  value: latest?.d1 != null ? `${latest.d1}%` : "—",  sub: t("analytics.latestCohort"),       color: latest?.d1 >= 60 ? "var(--teal)" : latest?.d1 >= 30 ? "var(--amber)" : "var(--rose)" },
                { label: t("analytics.day7Retention"),  value: latest?.d7 != null ? `${latest.d7}%` : "—",  sub: t("analytics.latestCohort"),       color: latest?.d7 >= 60 ? "var(--teal)" : latest?.d7 >= 30 ? "var(--amber)" : "var(--rose)" },
                { label: t("analytics.avg30dRetention"), value: isNaN(avg30) ? "—" : `${avg30.toFixed(0)}%`, sub: t("analytics.cohortsTracked", { n: cohorts.length }), color: avg30 >= 60 ? "var(--teal)" : avg30 >= 30 ? "var(--amber)" : "var(--rose)" },
              ].map(m => (
                <div key={m.label} style={{ textAlign: "center", padding: 14, background: "var(--surface)", borderRadius: 10, border: "1px solid var(--border)" }}>
                  <div style={{ fontFamily: "var(--fd)", fontSize: 22, fontWeight: 900, color: m.color }}>{m.value}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink)", marginTop: 4 }}>{m.label}</div>
                  <div style={{ fontSize: 10, color: "var(--ghost)", marginTop: 2 }}>{m.sub}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Retention Cohorts — clickable rows */}
      <div className="card">
        <div className="card-hdr">
          <div className="card-ttl"><div className="card-ico">🔁</div>{t("analytics.retentionCohorts")}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11, color: "var(--ghost)" }}>{t("analytics.clickForDetails")}</span>
            <span className="badge bg-teal">PostHog</span>
          </div>
        </div>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr><th>Cohort</th><th>Users</th><th>D1</th><th>D7</th><th>D14</th><th>D30</th></tr>
            </thead>
            <tbody>
              {cohorts.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div style={{ padding: "24px 14px", textAlign: "center" }}>
                      <div style={{ fontSize: 28, marginBottom: 8 }}>📊</div>
                      <div style={{ fontSize: 13, color: "var(--muted)", fontWeight: 600, marginBottom: 4 }}>{t("analytics.noRetentionData")}</div>
                      <div style={{ fontSize: 11, color: "var(--ghost)" }}>{t("analytics.connectPostHog")}</div>
                    </div>
                  </td>
                </tr>
              ) : cohorts.map(r => {
                const retCell = (v) =>
                  v === null
                    ? <span style={{ background: "#f3f4f6", color: "#6b7280", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10, display: "inline-block" }}>—</span>
                    : v >= 60
                    ? <span style={{ background: "#d1fae5", color: "#065f46", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10, display: "inline-block" }}>{v}%</span>
                    : <span style={{ background: "var(--amber-lo)", color: "#92400e", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10, display: "inline-block" }}>{v}%</span>;
                return (
                  <tr key={r.cohort}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelCohort(r)}
                    onMouseEnter={e => { e.currentTarget.style.background = "var(--teal-dim)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ""; }}>
                    <td style={{ fontWeight: 600 }}>{r.cohort}</td>
                    <td style={{ fontFamily: "var(--fm)" }}>{r.users}</td>
                    <td>{retCell(r.d1 ?? null)}</td>
                    <td>{retCell(r.d7 ?? null)}</td>
                    <td>{retCell(r.d14 ?? null)}</td>
                    <td>{retCell(r.d30 ?? null)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Book Downloads ─────────────────────────────────────────────────── */}
      {(() => {
        const bookStats = bd?.stats ?? {};
        const downloads = bd?.downloads ?? [];
        const byPlatform = bookStats.byPlatform ?? [];
        return (
          <>
            {/* Summary cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginTop: 8 }}>
              {[
                { label: "📥 Total Downloads",  value: (bookStats.totalDownloads ?? 0).toLocaleString(), color: "var(--teal)" },
                { label: "👥 Unique Downloaders", value: (bookStats.uniqueUsers ?? 0).toLocaleString(),  color: "var(--blue,#2563eb)" },
                ...byPlatform.map(p => ({
                  label: `${p.platform === "web" ? "🌐" : "📱"} ${p.platform === "web" ? "Web" : "Mobile"}`,
                  value: p.count.toLocaleString(),
                  color: "var(--ink)",
                })),
              ].map(m => (
                <div key={m.label} style={{ textAlign: "center", padding: 16, background: "var(--surface)", borderRadius: 10, border: "1px solid var(--border)" }}>
                  <div style={{ fontFamily: "var(--fd)", fontSize: 26, fontWeight: 900, color: m.color }}>{m.value}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink)", marginTop: 5 }}>{m.label}</div>
                </div>
              ))}
            </div>

            {/* Downloads table */}
            <div className="card">
              <div className="card-hdr">
                <div className="card-ttl"><div className="card-ico">📚</div>Free Book Downloads</div>
                <span style={{ fontSize: 11, color: "var(--ghost)" }}>{(bd?.total ?? 0).toLocaleString()} total</span>
              </div>
              <div className="tbl-wrap">
                <table>
                  <thead>
                    <tr><th>User</th><th>Email</th><th>Platform</th><th>Downloaded At</th></tr>
                  </thead>
                  <tbody>
                    {downloads.length === 0 ? (
                      <tr><td colSpan={4} style={{ padding: "24px", textAlign: "center", color: "var(--ghost)" }}>No downloads yet</td></tr>
                    ) : downloads.map(d => (
                      <tr key={d.id}>
                        <td style={{ fontWeight: 600 }}>{d.user?.name ?? "—"}</td>
                        <td style={{ color: "var(--muted)", fontSize: 12 }}>{d.user?.email ?? "—"}</td>
                        <td>
                          <span style={{
                            background: d.platform === "web" ? "var(--teal-dim,#f0fdf4)" : "#eff6ff",
                            color: d.platform === "web" ? "var(--teal)" : "#2563eb",
                            fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                          }}>
                            {d.platform === "web" ? "🌐 Web" : "📱 Mobile"}
                          </span>
                        </td>
                        <td style={{ fontSize: 12, color: "var(--muted)" }}>
                          {new Date(d.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        );
      })()}

      {/* Modals */}
      {selFunnel   && <FunnelDetailModal   step={selFunnel}      allFunnel={funnel}      onClose={() => setSelFunnel(null)}   />}
      {selPlatform && <PlatformDetailModal platform={selPlatform} allPlatforms={platforms} onClose={() => setSelPlatform(null)} />}
      {selCohort   && <RetentionCohortModal cohort={selCohort}   allCohorts={cohorts}    onClose={() => setSelCohort(null)}   />}
    </div>
  );
}
